import { setCookies } from '@acorns/web-utils'
import { ExecutionResult } from 'graphql'
import { object, string } from 'yup'

import { AuthType, AuthenticateMutation } from 'generated/graphql'

import { isValidEmail, isWhitelisted } from '../../utils/login-utils'
import {
  MfaScreenViewed,
  loadingTimeLogIn,
  loadingTimeLogInSuccess,
  logInNext,
} from '../../utils/segment'
import { getUdidForAuthentication } from '../../utils/udid-utils'

export type Values = {
  email: string
  password: string
}

// at this point, we are just validating that user has entered text into both fields, not that it is an email format
export const validationSchema = object().shape({
  email: string().required(),
  password: string().required(),
})

export const emailToPrefill = document.cookie
  .split(';')
  .filter((cookie) => cookie.includes('emailToPrefill'))
  .toString()
  .split('=')
  .pop()

// Used by Formik to set initial form values
export const mapPropsToValues = (props) => {
  const { email = '', password = '' } = props

  return {
    email: emailToPrefill ? emailToPrefill : email,
    password,
  }
}

export const resolveRedirect = (
  defaultRedirect: string,
  referrer?: string,
): string => {
  let redirect = defaultRedirect
  if (
    referrer &&
    !referrer.includes(window.location.host) &&
    isWhitelisted(referrer)
  ) {
    redirect = referrer
  } else {
    const { pathname, search } = window.location

    redirect = `${redirect}${pathname}`

    if (search) {
      redirect = `${redirect}${search}`
    }
  }

  return redirect
}

export const handleFormSubmit = async (
  values,
  { props, setSubmitting }: any,
) => {
  const preservedRedirect = props.location.pathname + props.location.search
  const preservedQueryParams = props.location.search

  if (!isValidEmail(values.email)) {
    setSubmitting(false)
    props.setIsValidationModalOpen(true)
    return
  }
  props.analytics.track('Button Tapped', logInNext)
  props.analytics.track(
    'Time Analysis',
    loadingTimeLogIn(Math.floor((Date.now() - props.timeOnLogInView) / 1000)),
  )
  setSubmitting(true)
  const trackLoadingTimeLogInSuccess = () =>
    props.analytics.track(
      'Time Analysis',
      loadingTimeLogInSuccess(
        Math.floor((Date.now() - props.timeOnLogInView) / 1000),
      ),
    )

  values.udid = getUdidForAuthentication()

  if (!props.trustThisDeviceWebIsEnabled || props.isOAuth) {
    // Append an arbitrary string to the udid when "trust this device" is disabled as a
    // small hack to always force MFA, even if the device/udid was previously trusted.
    values.udid += '__remember_me_disabled'
  }

  return props
    .login({
      variables: {
        input: {
          ...values,
        },
        oauth: props.isOAuth ?? false,
      },
    })
    .then((result: ExecutionResult<AuthenticateMutation>) => {
      if (result.data.authenticate.__typename === 'SMSAuthChallenge') {
        const mode = AuthType.Phone
        const challengeId = result.data.authenticate.id
        const phoneNumber =
          '(•••) ••• ' + result.data.authenticate.maskedPhoneNumber
        const alternateAuthenticatorId =
          result.data.authenticate.alternateAuthenticators.find(
            (authenticator) => authenticator?.type !== mode,
          )?.id

        props.analytics.track(
          'Screen Viewed',
          MfaScreenViewed(props.location.pathname),
        )
        props.history.push({
          pathname: props.isOAuth ? '/oauth/mfa-challenge' : '/mfa-challenge',
          data: {
            values,
            challengeId,
            phoneNumber,
            mode,
            alternateAuthenticatorId,
            preservedRedirect,
            preservedQueryParams,
          },
        })
        trackLoadingTimeLogInSuccess()
      } else if (result.data.authenticate.__typename === 'EmailAuthChallenge') {
        const mode = AuthType.Email
        const challengeId = result.data.authenticate.id
        const email = result.data.authenticate.maskedEmail
        const alternateAuthenticatorId =
          result.data.authenticate.alternateAuthenticators.find(
            (authenticator) => authenticator.type !== mode,
          )?.id
        props.analytics.track(
          'Screen Viewed',
          MfaScreenViewed(props.location.pathname),
        )
        props.history.push({
          pathname: props.isOAuth ? '/oauth/mfa-challenge' : '/mfa-challenge',
          data: {
            values,
            challengeId,
            email,
            mode,
            alternateAuthenticatorId,
            preservedRedirect,
            preservedQueryParams,
          },
        })
        trackLoadingTimeLogInSuccess()
      } else if (result.data.authenticate.__typename === 'AuthSession') {
        setCookies(result.data.authenticate)

        window.location.href = resolveRedirect(
          props.env.get('defaultRedirect'),
          document.referrer,
        )
        trackLoadingTimeLogInSuccess()
      } else {
        props.setException(result.data.authenticate.__typename)
        props.setIsModalOpen(true)
        if (result.data.authenticate.__typename === 'UserSuspendedException') {
          props.setExceptionMessage(result.data.authenticate.message)
        }
      }
    })
    .finally(() => setSubmitting(false))
}
